<template>
  <RevDrawer
    :close-button-label="i18n(translations.close)"
    :has-back-button="!!currentFormType"
    :name="MODAL_NAME"
    :title="modalTitle"
    @back="cleanHash"
    @close="cleanHash"
  >
    <template #body>
      <RevList v-if="!currentFormType" :has-external-borders="false">
        <RevListItemInteractive @click="handleNavigation(FORM_TYPES.UPDATE)">
          <template #label>
            {{ i18n(translations.updateExistingListingsPricesAndStock) }}
          </template>
          <template #suffix>
            <IconChevronRight />
          </template>
        </RevListItemInteractive>
        <RevListItemInteractive @click="handleNavigation(FORM_TYPES.IMPORT)">
          <template #label>
            {{ i18n(translations.importListingsThroughCsv) }}
          </template>
          <template #suffix>
            <IconChevronRight />
          </template>
        </RevListItemInteractive>
        <RevListItemInteractive
          @click="handleNavigation(FORM_TYPES.IMPORT_NEW)"
        >
          <template #label>
            {{ i18n(translations.importNewProductListingsThroughCsv) }}
          </template>
          <template #suffix>
            <IconChevronRight />
          </template>
        </RevListItemInteractive>
        <RevListItemInteractive @click="handleNavigation(FORM_TYPES.EXPORT)">
          <template #label>
            {{ i18n(translations.exportListingsInCsv) }}
          </template>
          <template #suffix>
            <IconChevronRight />
          </template>
        </RevListItemInteractive>
        <RevListItemInteractive
          v-if="isFeatureEnabled('isDsaEnabled')"
          @click="handleNavigation(FORM_TYPES.UPDATES_CONTACTS)"
        >
          <template #label>
            {{ i18n(translations.updateContacts) }}
          </template>
          <template #suffix>
            <IconChevronRight />
          </template>
        </RevListItemInteractive>
      </RevList>

      <Form
        v-else
        :publication-state
        :type="currentFormType"
        @result="handleResult"
        @submitted="handleSubmitted"
      />
    </template>
  </RevDrawer>
</template>

<script lang="ts" setup>
import { navigateTo, onBeforeRouteLeave, useRoute } from '#imports'
import { computed, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevDrawer } from '@ds/components/Drawer'
import { RevList } from '@ds/components/List'
import { RevListItemInteractive } from '@ds/components/ListItemInteractive'
import { closeModal, openModal } from '@ds/components/ModalBase'
import { IconChevronRight } from '@ds/icons/IconChevronRight'

import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'
import { PUBLICATION_STATE } from '~/scopes/listings/Listings.constants'

import Form from './Form/Form.vue'
import {
  FORM_TYPES,
  type FormTypes,
  HASH_NAME,
  MODAL_NAME,
} from './ImportExportDrawer.constants'
import translations from './ImportExportDrawer.translations'

const i18n = useI18n()
const route = useRoute()
const tracking = useTracking()
const { openSuccessToast, openErrorToast } = useTheToast()
const { isFeatureEnabled } = useBusinessFeaturesStore()
const publicationState = computed(() => {
  return (route.meta?.publicationState as string) || PUBLICATION_STATE.ONLINE
})
const currentFormType = ref<FormTypes[keyof FormTypes] | ''>('')

const alreadyLeavingRoute = ref(false)

const MODAL_TITLES = {
  [FORM_TYPES.EXPORT]: i18n(translations.exportListings),
  [FORM_TYPES.IMPORT]: i18n(translations.importListings),
  [FORM_TYPES.IMPORT_NEW]: i18n(translations.importNewProductListings),
  [FORM_TYPES.UPDATE]: i18n(translations.updateListings),
  [FORM_TYPES.UPDATES_CONTACTS]: i18n(translations.updateContacts),
}

const modalTitle = computed(() => {
  return !currentFormType.value
    ? i18n(translations.modalTitle)
    : MODAL_TITLES[currentFormType.value]
})

async function handleNavigation(formType: string) {
  tracking.trackClick({
    page_type: 'listings',
    zone: 'listings_action_panel',
    name: formType,
  })

  await navigateTo({
    ...route,
    query: {
      ...route.query,
    },
    hash: `#${HASH_NAME}=${formType}`,
  })
}

async function cleanHash() {
  currentFormType.value = ''

  if (alreadyLeavingRoute.value) {
    return
  }

  await navigateTo({
    ...route,
    query: route.query,
    hash: '',
    replace: true,
  })
}

function handleSubmitted() {
  closeModal(MODAL_NAME)
}

function handleResult(result: string) {
  switch (result) {
    case 'export/success':
      openSuccessToast({
        title: i18n(translations.toastExportSuccessTitle),
        content: i18n(translations.toastExportSuccessText),
      })
      break

    case 'export/error':
      openErrorToast({
        title: i18n(translations.toastActionsErrorTitle),
        content: i18n(translations.toastExportErrorText),
      })
      break

    case 'import/success':
      openSuccessToast({
        title: i18n(translations.toastImportSuccessTitle),
        content: i18n(translations.toastImportSuccessText),
      })
      break

    case 'import/error':
      openErrorToast({
        title: i18n(translations.toastActionsErrorTitle),
        content: i18n(translations.toastImportErrorText),
      })
      break

    case 'update/success':
      openSuccessToast({
        title: i18n(translations.toastUpdateSuccessTitle),
        content: i18n(translations.toastUpdateSuccessText),
      })
      break

    case 'update/error':
      openErrorToast({
        title: i18n(translations.toastActionsErrorTitle),
        content: i18n(translations.toastUpdateErrorText),
      })
      break

    case 'update-contacts/success':
      openSuccessToast({
        title: i18n(translations.toastUpdateSuccessTitle),
        content: i18n(translations.toastUpdateSuccessText),
      })
      break

    case 'update-contacts/error':
      openErrorToast({
        title: i18n(translations.toastActionsErrorTitle),
        content: i18n(translations.toastUpdateErrorText),
      })
      break

    default:
      break
  }
}

watch(
  () => route.hash,
  () => {
    if (route.hash.includes(HASH_NAME)) {
      const formType = route.hash.split('=')[1]

      if (
        Object.values(FORM_TYPES).includes(
          formType as FormTypes[keyof FormTypes],
        )
      ) {
        currentFormType.value = formType as FormTypes[keyof FormTypes]
        openModal(MODAL_NAME)
      }
    } else {
      currentFormType.value = ''
    }
  },
  {
    immediate: true,
  },
)

// Whenever navigation to a different route (such as to the batch listing creation page) takes place, the modal is closed.
// Closing the modal unfortunately triggers a second navigation, which clashes with the first navigation to a different route.
// To prevent this double navigation we set a temporary value to indicate the first (correct) navigation is already in place,
// we can then based on this value ignore the second navigation call.
onBeforeRouteLeave(() => {
  alreadyLeavingRoute.value = true
})
</script>
